/**
 * Authenticated fetch wrapper for HTTP-only cookie authentication
 * Use this for direct fetch calls that need authentication
 */

const API_BASE_URL = process.env.NODE_ENV === 'development'
  ? `http://${window.location.hostname}:5005/api`
  : `https://${window.location.hostname}/api`;

interface FetchOptions extends RequestInit {
  headers?: Record<string, string>;
}

/**
 * Make an authenticated fetch request
 * Cookies are sent automatically with credentials: 'include'
 */
export const authFetch = async (url: string, options: FetchOptions = {}): Promise<Response> => {
  // Normalize URL - ensure it starts with /api if it's a relative path
  const normalizedUrl = url.startsWith('http')
    ? url
    : url.startsWith('/api')
      ? `${API_BASE_URL}${url.slice(4)}`
      : `${API_BASE_URL}${url.startsWith('/') ? url : `/${url}`}`;

  const defaultHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Remove Authorization header if present (not needed with cookies)
  if (defaultHeaders.Authorization) {
    delete defaultHeaders.Authorization;
  }

  const mergedOptions: RequestInit = {
    credentials: 'include', // Send cookies with request
    ...options,
    headers: defaultHeaders,
  };

  return fetch(normalizedUrl, mergedOptions);
};

/**
 * Make an authenticated GET request
 */
export const authGet = async <T = unknown>(url: string): Promise<T> => {
  const response = await authFetch(url, { method: 'GET' });
  return response.json();
};

/**
 * Make an authenticated POST request
 */
export const authPost = async <T = unknown>(url: string, data: unknown): Promise<T> => {
  const response = await authFetch(url, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.json();
};

/**
 * Make an authenticated PUT request
 */
export const authPut = async <T = unknown>(url: string, data: unknown): Promise<T> => {
  const response = await authFetch(url, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  return response.json();
};

/**
 * Make an authenticated DELETE request
 */
export const authDelete = async <T = unknown>(url: string): Promise<T> => {
  const response = await authFetch(url, { method: 'DELETE' });
  return response.json();
};

export default authFetch;
